import { FeatureType, ItemType, BillingType } from './pricing.types.js';
import { SubscriptionTier } from '@fabric/woody-client';
import { getCompatEnv } from '@fabric-app/compat/capabilities/environment';

const FEATURES = {
  "create-notes-save-links-files": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "create-notes-save-links-files",
    description: "Create notes, save links & files"
  },
  "storage-space-250mb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "250MB storage space"
  },
  "storage-space-100gb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "100GB storage space"
  },
  "storage-space-50gb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "50GB storage space"
  },
  "storage-space-2tb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "2TB storage space"
  },
  "storage-space-4tb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "4TB storage space"
  },
  "file-upload-10mb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "file-upload",
    description: "10MB file upload"
  },
  "file-upload-50mb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "file-upload",
    description: "50MB file upload"
  },
  "file-upload-5gb": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "file-upload",
    description: "5GB file upload"
  },
  "thinking-credits-100": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "thinking-credits",
    description: "100 thinking credits"
  },
  "thinking-credits-500": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "thinking-credits",
    description: "500 thinking credits"
  },
  "thinking-credits-1500": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "thinking-credits",
    description: "1,500 thinking credits"
  },
  "sync-across-devices": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "sync-across-devices",
    description: "Sync across your devices"
  },
  "powerful-ai-search": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "powerful-ai-search",
    description: "Powerful AI search"
  },
  "ai-assistant": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-assistant",
    description: "AI assistant"
  },
  "cloud-backup-encryption": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "cloud-backup-encryption",
    description: "Cloud backup & encryption"
  },
  sharing: {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "sharing",
    description: "Sharing"
  },
  publishing: {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "publishing",
    description: "Publishing"
  },
  "ai-document-editor": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-document-editor",
    description: "AI document editor"
  },
  "ai-voice-meeting-recording": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-voice-meeting-recording",
    description: "AI voice & meeting recording"
  },
  "audio-video-transcription": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "audio-video-transcription",
    description: "Audio & video transcription"
  },
  "ai-suggestions": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-suggestions",
    description: "AI suggestions"
  },
  "data-connections": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "data-connections",
    description: "Data connections"
  },
  "ai-voice-notes": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-voice-notes",
    description: "AI voice notes"
  },
  "ai-models-access": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "ai-models-access",
    description: "Access to all the top AI models"
  },
  "smart-collections": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "smart-collections",
    description: "Smart collections"
  },
  "fabric-labs": {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "fabric-labs",
    description: "Try pre-release features first"
  }
};
const getPlans = () => {
  const PRICING = getCompatEnv().PRICING;
  return {
    [SubscriptionTier.FREE]: {
      tier: SubscriptionTier.FREE,
      title: "Free",
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 0,
        billedYearly: 0
      },
      order: 1e3,
      purchaseable: true,
      entitlements: {
        maxFileSizeMB: 10,
        maxTotalStorageSizeMB: 250,
        creditCentsMonthly: 1e4,
        integrationsEnabled: false,
        bulkImportEnabled: false,
        audioTranscriptionEnabled: false,
        aiVoiceAndMeetingRecordingEnabled: false,
        recapsEnabled: false,
        modelSwitchingEnabled: false,
        apiKeysEnabled: false,
        fabricLabsEnabled: false
      },
      items: [
        FEATURES["create-notes-save-links-files"],
        FEATURES["storage-space-250mb"],
        FEATURES["file-upload-10mb"],
        FEATURES["thinking-credits-100"],
        FEATURES["sync-across-devices"],
        FEATURES["powerful-ai-search"],
        FEATURES["ai-assistant"],
        FEATURES["cloud-backup-encryption"],
        FEATURES["sharing"],
        FEATURES["publishing"],
        FEATURES["ai-document-editor"]
      ]
    },
    [SubscriptionTier.BASIC_LEGACY]: {
      tier: SubscriptionTier.BASIC_LEGACY,
      title: "Basic (2024)",
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 6,
        billedYearly: 60,
        stripePlanIdMonthly: PRICING.BASIC_MONTHLY_PLAN_ID,
        stripePlanIdYearly: PRICING.BASIC_YEARLY_PLAN_ID,
        revenueCatOfferingId: PRICING.BASIC_OFFERING_ID
      },
      order: 2e3,
      purchaseable: false,
      entitlements: {
        maxFileSizeMB: 5e6,
        maxTotalStorageSizeMB: 1e5,
        creditCentsMonthly: 5e4,
        integrationsEnabled: false,
        bulkImportEnabled: true,
        audioTranscriptionEnabled: true,
        aiVoiceAndMeetingRecordingEnabled: true,
        recapsEnabled: true,
        modelSwitchingEnabled: false,
        apiKeysEnabled: false,
        fabricLabsEnabled: false
      },
      items: [
        FEATURES["storage-space-100gb"],
        FEATURES["file-upload-50mb"],
        FEATURES["thinking-credits-500"],
        FEATURES["ai-voice-meeting-recording"],
        FEATURES["audio-video-transcription"],
        FEATURES["ai-suggestions"]
      ]
    },
    [SubscriptionTier.BASIC]: {
      tier: SubscriptionTier.BASIC,
      title: "Basic",
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 6,
        billedYearly: 56,
        stripePlanIdMonthly: PRICING.BASIC_MONTHLY_PLAN_ID,
        stripePlanIdYearly: PRICING.BASIC_YEARLY_PLAN_V2_ID,
        revenueCatOfferingId: PRICING.BASIC_OFFERING_ID
      },
      order: 2e3,
      purchaseable: true,
      entitlements: {
        maxFileSizeMB: 50,
        maxTotalStorageSizeMB: 5e4,
        creditCentsMonthly: 5e4,
        integrationsEnabled: false,
        bulkImportEnabled: true,
        audioTranscriptionEnabled: true,
        aiVoiceAndMeetingRecordingEnabled: true,
        recapsEnabled: true,
        modelSwitchingEnabled: false,
        apiKeysEnabled: false,
        fabricLabsEnabled: false
      },
      items: [
        FEATURES["storage-space-50gb"],
        FEATURES["file-upload-50mb"],
        FEATURES["thinking-credits-500"],
        FEATURES["ai-voice-meeting-recording"],
        FEATURES["audio-video-transcription"],
        FEATURES["ai-suggestions"]
      ],
      itemsExtended: [
        FEATURES["create-notes-save-links-files"],
        FEATURES["storage-space-50gb"],
        FEATURES["file-upload-50mb"],
        FEATURES["thinking-credits-500"],
        FEATURES["sync-across-devices"],
        FEATURES["powerful-ai-search"],
        FEATURES["ai-assistant"],
        FEATURES["cloud-backup-encryption"],
        FEATURES["sharing"],
        FEATURES["publishing"],
        FEATURES["ai-document-editor"],
        FEATURES["ai-voice-meeting-recording"],
        FEATURES["audio-video-transcription"]
      ]
    },
    [SubscriptionTier.PRO]: {
      tier: SubscriptionTier.PRO,
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 16,
        billedYearly: 150,
        billed3Years: 300,
        stripePlanIdMonthly: PRICING.PRO_MONTHLY_PLAN_V2_ID,
        stripePlanIdYearly: PRICING.PRO_YEARLY_PLAN_ID,
        stripePlanId3Years: PRICING.PRO_3_YEARS_PLAN_ID,
        revenueCatOfferingId: PRICING.PRO_OFFERING_ID
      },
      order: 3e3,
      purchaseable: true,
      entitlements: {
        integrationsEnabled: true,
        bulkImportEnabled: true,
        maxFileSizeMB: 5e3,
        maxTotalStorageSizeMB: 2e6,
        creditCentsMonthly: 15e4,
        audioTranscriptionEnabled: true,
        aiVoiceAndMeetingRecordingEnabled: true,
        recapsEnabled: true,
        modelSwitchingEnabled: true,
        apiKeysEnabled: true,
        fabricLabsEnabled: true
      },
      title: "Pro",
      isMostPopular: true,
      items: [
        FEATURES["storage-space-2tb"],
        FEATURES["file-upload-5gb"],
        FEATURES["thinking-credits-1500"],
        FEATURES["data-connections"],
        FEATURES["ai-models-access"],
        FEATURES["fabric-labs"]
      ],
      itemsExtended: [
        FEATURES["create-notes-save-links-files"],
        FEATURES["storage-space-2tb"],
        FEATURES["file-upload-5gb"],
        FEATURES["thinking-credits-1500"],
        FEATURES["sync-across-devices"],
        FEATURES["powerful-ai-search"],
        FEATURES["ai-suggestions"],
        FEATURES["data-connections"],
        FEATURES["cloud-backup-encryption"],
        FEATURES["sharing"],
        FEATURES["publishing"],
        FEATURES["ai-document-editor"],
        FEATURES["ai-voice-meeting-recording"],
        FEATURES["audio-video-transcription"],
        FEATURES["fabric-labs"],
        FEATURES["ai-models-access"]
      ]
    },
    [SubscriptionTier.MAX_LEGACY]: {
      tier: SubscriptionTier.MAX_LEGACY,
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 39,
        billedYearly: 390,
        stripePlanIdMonthly: PRICING.MAX_MONTHLY_PLAN_ID,
        stripePlanIdYearly: PRICING.MAX_YEARLY_PLAN_ID,
        revenueCatOfferingId: PRICING.MAX_OFFERING_ID
      },
      order: 4e3,
      purchaseable: false,
      entitlements: {
        integrationsEnabled: true,
        bulkImportEnabled: true,
        maxFileSizeMB: 5e3,
        maxTotalStorageSizeMB: 2e6,
        creditCentsMonthly: 1e4,
        audioTranscriptionEnabled: true,
        aiVoiceAndMeetingRecordingEnabled: true,
        recapsEnabled: true,
        modelSwitchingEnabled: true,
        apiKeysEnabled: true,
        fabricLabsEnabled: true
      },
      title: "Max (2024)",
      items: [
        FEATURES["storage-space-4tb"],
        FEATURES["ai-voice-notes"],
        FEATURES["ai-assistant"],
        FEATURES["data-connections"],
        FEATURES["file-upload-5gb"],
        FEATURES["ai-models-access"],
        FEATURES["smart-collections"]
      ]
    },
    [SubscriptionTier.LIFETIME]: {
      tier: SubscriptionTier.LIFETIME,
      title: "Believer (Lifetime)",
      prices: {
        type: BillingType.Recurring,
        billedMonthly: 0,
        billedYearly: 0
      },
      order: 5e3,
      purchaseable: false,
      entitlements: {
        integrationsEnabled: true,
        bulkImportEnabled: true,
        maxFileSizeMB: 5e3,
        maxTotalStorageSizeMB: 2e6,
        creditCentsMonthly: 15e4,
        audioTranscriptionEnabled: true,
        aiVoiceAndMeetingRecordingEnabled: true,
        recapsEnabled: true,
        modelSwitchingEnabled: true,
        apiKeysEnabled: true,
        fabricLabsEnabled: true
      },
      items: [
        {
          type: ItemType.Feature,
          featureType: FeatureType.Highlighted,
          has: true,
          key: "everything-in-prev-plan",
          description: "Everything in the Pro plan, forever"
        },
        {
          type: ItemType.Separator
        },
        {
          type: ItemType.Feature,
          featureType: FeatureType.Regular,
          has: true,
          key: "believer-badge-in-profile",
          description: "Believer badge in profile"
        },
        {
          type: ItemType.Feature,
          featureType: FeatureType.Regular,
          has: true,
          key: "vip-access-to-the-team",
          description: "VIP access to the team"
        },
        {
          type: ItemType.Feature,
          featureType: FeatureType.Regular,
          has: true,
          key: "be-a-founding-member-of-fabric",
          description: "Be a founding member of Fabric"
        },
        {
          type: ItemType.Feature,
          featureType: FeatureType.Regular,
          has: true,
          key: "discounted-price-lock",
          description: "Lock-in a discounted price"
        }
      ]
    }
  };
};
const getExtensiveLifetimePlan = () => {
  const plans = getPlans();
  return {
    ...plans[SubscriptionTier.LIFETIME],
    // same as pro plan
    items: [
      ...plans[SubscriptionTier.PRO].items.map((item) => {
        if (item.type === ItemType.Feature && item.description.includes("AI assistant")) {
          return {
            ...item,
            description: "AI assistant"
          };
        }
        return item;
      }, [])
    ]
  };
};
const marketingItems = [
  {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "storage-space",
    description: "More storage space"
  },
  {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "file-upload",
    description: "Larger file uploads"
  },
  {
    type: ItemType.Feature,
    featureType: FeatureType.Regular,
    has: true,
    key: "thinking-credits",
    description: "More thinking credits"
  },
  FEATURES["ai-suggestions"],
  FEATURES["data-connections"],
  FEATURES["ai-voice-notes"],
  FEATURES["audio-video-transcription"],
  FEATURES["fabric-labs"],
  FEATURES["ai-models-access"]
];
const getPlanExtendedItems = (plan) => {
  if (plan.itemsExtended) {
    return plan.itemsExtended;
  }
  const prevPlans = Object.values(getPlans()).sort((a, b) => a.order - b.order).filter((p) => p.purchaseable && p.order < plan.order);
  const items = plan.items;
  for (const prevPlan of prevPlans) {
    for (const item of prevPlan.items) {
      if (item.type === ItemType.Feature && "key" in item && !items.some((i) => "key" in i && i.key === item.key)) {
        items.push(item);
      }
    }
  }
  return items;
};
const getPreviousPlan = (plan) => {
  const plans = Object.values(getPlans()).sort((a, b) => b.order - a.order);
  return plans.find((p) => p.purchaseable && p.order < plan.order);
};

export { getExtensiveLifetimePlan, getPlanExtendedItems, getPlans, getPreviousPlan, marketingItems };
//# sourceMappingURL=pricing.config.js.map
